import * as React from 'react'
import * as moment from 'moment'
import { useCargoFluxShipmentsQuery } from '../../hooks/queries/cargoflux_shipments'
import { getCargoFluxShipmentsExportUrl } from '../../api/cargoflux_shipments'
import { JobNumberShape } from '../../api/api_data_types'
import ErrorMessage from '../job_plannings/list_app/ErrorMessage'

type CargoFluxShipmentsProps = {
  jobnumber: JobNumberShape
}

type ShipmentData = {
  id: number
  nav_shipment_id: string
  order_number: string
  status: string
  company: string
  city: string
  tracking_number: string
  track_trace_email_sent_at?: string | null
  price: number | string | null
  created_at: string
  raw_shipment_data: any
}

function handleExportClick(jobnumberId: number) {
  const exportUrl = getCargoFluxShipmentsExportUrl(jobnumberId)
  window.location.href = exportUrl
}

function formatPrice(price: number | string | null): string {
  if (!price) return '-'
  const numPrice = typeof price === 'string' ? parseFloat(price) : price
  if (isNaN(numPrice)) return '-'
  return `${numPrice.toFixed(2)} DKK`
}

function formatDateTime(value?: string | null): string {
  return value ? moment(value).format('DD-MM-YYYY HH:mm') : '-'
}

export default function CargoFluxShipments({ jobnumber }: CargoFluxShipmentsProps) {
  const { isLoading, isError, data, error } = useCargoFluxShipmentsQuery(jobnumber.id)

  if (isError) {
    return <ErrorMessage isError={isError} error={error} />
  }

  const shipments: ShipmentData[] = data?.data || []

  return (
    <div className="cargoflux-shipments">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>CargoFlux Forsendelser</h5>
        {shipments.length > 0 && (
          <button
            className="btn btn-primary"
            onClick={() => handleExportClick(jobnumber.id)}
          >
            <i className="fa fa-download mr-2" />
            Download Excel
          </button>
        )}
      </div>

      {isLoading && (
        <div className="text-center">
          <i className="fa fa-spinner fa-spin fa-2x" />
        </div>
      )}

      {!isLoading && shipments.length === 0 && (
        <div className="text-center font-italic text-muted">
          Ingen forsendelser fundet for denne ordre
        </div>
      )}

      {!isLoading && shipments.length > 0 && (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Status</th>
                <th>Firma</th>
                <th>By</th>
                <th>Pris</th>
                <th>Tracking</th>
                <th>Email sendt</th>
                <th>Oprettet</th>
              </tr>
            </thead>
            <tbody>
              {shipments.map((shipment) => {
                const rawData = shipment.raw_shipment_data || {}
                const shipmentData = rawData.shipment || {}
                const recipient = rawData.recipient || {}
                const reference = shipmentData.reference || shipment.nav_shipment_id || ''
                const company = recipient.company_name || shipment.company || ''
                const city = recipient.city || shipment.city || ''

                return (
                  <tr key={shipment.id}>
                    <td>{reference}</td>
                    <td>{shipment.status}</td>
                    <td>{company}</td>
                    <td>{city}</td>
                    <td>{formatPrice(shipment.price)}</td>
                    <td>
                      {shipment.tracking_number ? (
                        <a href={shipment.tracking_number} target="_blank" rel="noopener noreferrer">
                          <i className="fa fa-icon fa-external-link-alt" />
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{formatDateTime(shipment.track_trace_email_sent_at)}</td>
                    <td>{moment(shipment.created_at).format('DD-MM-YYYY')}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
